////////////////////////////////////////////////////////////////////////////
//
// Copyright 2023 Realm Inc.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
//
////////////////////////////////////////////////////////////////////////////
export const enum SchemaMode {
  Automatic = 0,
  Immutable = 1,
  ReadOnly = 2,
  SoftResetFile = 3,
  HardResetFile = 4,
  AdditiveDiscovered = 5,
  AdditiveExplicit = 6,
  Manual = 7,
}
export const enum PropertyType {
  Int = 0,
  Bool = 1,
  String = 2,
  Data = 3,
  Date = 4,
  Float = 5,
  Double = 6,
  Object = 7,
  LinkingObjects = 8,
  Mixed = 9,
  ObjectId = 10,
  Decimal = 11,
  Uuid = 12,
  Required = 0,
  Nullable = 64,
  Array = 128,
  Set = 256,
  Dictionary = 512,
  Collection = 896,
  Flags = 960,
}
export const enum TableType {
  TopLevel = 0,
  Embedded = 1,
  TopLevelAsymmetric = 2,
}
export const enum ClientResetMode {
  Manual = 0,
  DiscardLocal = 1,
  Recover = 2,
  RecoverOrDiscard = 3,
}
export const enum DataType {
  Int = 0,
  Bool = 1,
  String = 2,
  Binary = 4,
  Mixed = 6,
  Timestamp = 8,
  Float = 9,
  Double = 10,
  Decimal = 11,
  Link = 12,
  LinkList = 13,
  ObjectId = 15,
  TypedLink = 16,
  Uuid = 17,
}
export const enum AuthProvider {
  Anonymous = 0,
  AnonymousNoReuse = 1,
  Facebook = 2,
  Google = 3,
  Apple = 4,
  Custom = 5,
  UsernamePassword = 6,
  Function = 7,
  ApiKey = 8,
}
export const enum MetadataMode {
  NoEncryption = 0,
  Encryption = 1,
  NoMetadata = 2,
}
export const enum LoggerLevel {
  All = 0,
  Trace = 1,
  Debug = 2,
  Detail = 3,
  Info = 4,
  Warn = 5,
  Error = 6,
  Fatal = 7,
  Off = 8,
}
export const enum HttpMethod {
  Get = 0,
  Post = 1,
  Patch = 2,
  Put = 3,
  Del = 4,
}
export const enum SyncSessionStopPolicy {
  Immediately = 0,
  LiveIndefinitely = 1,
  AfterChangesUploaded = 2,
}
export const enum SyncSessionState {
  Active = 0,
  Dying = 1,
  Inactive = 2,
  WaitingForAccessToken = 3,
}
export const enum SyncSessionConnectionState {
  Disconnected = 0,
  Connecting = 1,
  Connected = 2,
}
export const enum SyncErrorAction {
  NoAction = 0,
  ProtocolViolation = 1,
  ApplicationBug = 2,
  Warning = 3,
  Transient = 4,
  DeleteRealm = 5,
  ClientReset = 6,
  ClientResetNoRecovery = 7,
}
export const enum ProgressDirection {
  Upload = 0,
  Download = 1,
}
export const enum SyncUserState {
  LoggedOut = 0,
  LoggedIn = 1,
  Removed = 2,
}
export const enum SyncSubscriptionSetState {
  Uncommitted = 0,
  Pending = 1,
  Bootstrapping = 2,
  Complete = 3,
  Error = 4,
  Superseded = 5,
  AwaitingMark = 6,
}
export const enum WatchStreamState {
  NeedData = 0,
  HaveEvent = 1,
  HaveError = 2,
}
export const enum ProxyType {
  Http = 0,
  Https = 1,
}

// Wrapped types
export class Float {
  constructor(public value: number) {}
  valueOf() {
    return this.value;
  }
}
export class Status {
  public isOk: boolean;
  public code?: number;
  public reason?: string;
  constructor(isOk: boolean) {
    this.isOk = isOk;
  }
}
